import { convexQuery } from "@convex-dev/react-query";
import { isValidConvexId } from "@plasma/utils/convex-id";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { z } from "zod";

import {
  categoriesGetForMenu,
  itemsGetOne,
  locationsGetAllMine,
  modifiersGetAllGroupsForMenu,
  taxCategoriesGetAll,
} from "~/api-refs";
import { ItemDetailsForm } from "~/components/menus/items/detail/item-details-form";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useCurrentUser } from "~/integrations/vortex-auth/hooks";
import { toConvexTableId } from "~/lib/convex-table-id";

// V4.1: Search params for inline category creation - passed back from child routes
const itemSearchParams = z.object({
  newSalesCategoryId: z.string().optional(),
  newTaxCategoryId: z.string().optional(),
});

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/items/$itemId"
)({
  component: ItemDetailsPage,
  validateSearch: itemSearchParams,
});

function ItemDetailsPage() {
  const { itemId, menuId } = Route.useParams();
  const { newSalesCategoryId, newTaxCategoryId } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const userId = user?.id;

  // Validate ID formats before making any queries
  const validMenuId = isValidConvexId(menuId);
  const validItemId = isValidConvexId(itemId);
  const validIds = validMenuId && validItemId;

  const { data: item, isPending: itemLoading } = useQuery({
    ...convexQuery(
      itemsGetOne,
      validIds ? { itemId: toConvexTableId("items", itemId) } : "skip"
    ),
  });
  const { data: categoriesResult, isPending: categoriesLoading } = useQuery({
    ...convexQuery(
      categoriesGetForMenu,
      validIds
        ? {
            menuId: toConvexTableId("menus", menuId),
          }
        : "skip"
    ),
  });
  const { data: taxCategories, isPending: taxCategoriesLoading } = useQuery({
    ...convexQuery(taxCategoriesGetAll, validIds ? {} : "skip"),
  });
  const { data: modifiers, isPending: modifiersLoading } = useQuery({
    ...convexQuery(
      modifiersGetAllGroupsForMenu,
      validIds
        ? {
            menuId: toConvexTableId("menus", menuId),
          }
        : "skip"
    ),
  });
  const { data: locations, isPending: locationsLoading } = useQuery({
    ...convexQuery(locationsGetAllMine, validIds ? {} : "skip"),
  });

  if (!(validMenuId && validItemId)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{validMenuId ? "Item" : "Menu"} Not Found</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <span>
            The {validMenuId ? "item" : "menu"} ID in the URL is invalid or
            doesn't exist.
          </span>
          <Button asChild className="w-fit" size="sm" variant="outline">
            <Link to="/menus">Back to Menus</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (
    itemLoading ||
    categoriesLoading ||
    taxCategoriesLoading ||
    modifiersLoading ||
    locationsLoading ||
    !userId
  ) {
    return <LoadingPanel message="Loading item details..." />;
  }

  const categories = categoriesResult;

  // If item is null (deleted), show loading instead of 404 to allow navigation to complete
  if (item === null) {
    return <LoadingPanel message="Loading item details..." />;
  }

  if (!(item && categories && taxCategories && modifiers && locations)) {
    throw new Error("Required data not found");
  }

  // V4.1: Navigation callbacks for inline category creation
  const handleCreateSalesCategory = (name: string) => {
    void navigate({
      to: `/menus/${menuId}/items/${itemId}/create-sales-category`,
      search: { name },
    });
  };

  const handleCreateTaxCategory = (name: string) => {
    void navigate({
      to: `/menus/${menuId}/items/${itemId}/create-tax-category`,
      search: { name },
    });
  };

  const handleClearSearchParams = () => {
    void navigate({
      to: `/menus/${menuId}/items/${itemId}`,
      search: {},
      replace: true,
    });
  };

  return (
    <>
      <ItemDetailsForm
        categories={categories}
        itemData={item}
        locations={locations}
        menuId={menuId}
        modifierGroups={modifiers}
        newSalesCategoryId={newSalesCategoryId}
        newTaxCategoryId={newTaxCategoryId}
        onClearSearchParams={handleClearSearchParams}
        // V4.1: Route-based category creation
        onCreateSalesCategory={handleCreateSalesCategory}
        onCreateTaxCategory={handleCreateTaxCategory}
        taxCategories={taxCategories}
        userId={userId}
      />
      {/* Outlet for child routes (e.g., /delete modal, /create-sales-category, /create-tax-category) */}
      <Outlet />
    </>
  );
}
